import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';

import BaseButton from '../../../components/BaseButton';
import MessageModal from '../../../components/MessageModal';
import { fetchErrorsFile, postUploadData } from '../helpers';
import { ReactComponent as CheckIcon } from './../../../assets/icons/circle-check-icon.svg';
import { ReactComponent as WarningIcon } from './../../../assets/icons/warning-icon.svg';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  chooseLabel: string;
  uploadLabel: string;
  dataSet: any;
}

const FileUpload = ({ chooseLabel, uploadLabel, dataSet }: Props): React.ReactElement => {
  const [file, setFile] = useState<File | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [hasMappingIssues, setHasMappingIssues] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const fileInputField = useRef(null);
  const history = useHistory();
  const { oktaAuth } = useOktaAuth();

  useEffect(() => {
    setFile(null);
  }, [dataSet]);

  const onFileChange = (event: any) => {
    setFile(event.target.files[0]);
  };
  const doFileUpload = async () => {
    setHasError(false);
    setMessage('');
    setIsLoading(true);
    setShowModal(true);
    setHasMappingIssues(false);

    if (file) {
      const formData = new FormData();
      formData.append('dataSetType', dataSet.dataset);
      formData.append('dataSetFile', file);

      postUploadData(formData, oktaAuth)
        .then((response: any) => {
          if (response.data) {
            if (response.data.status === 'FAILURE' || response.data.status === 'FAILURE_WITH_VALIDATION_ERRORS') {
              handleError(response.data);
            } else {
              handleSuccess(response.data);
            }
          } else {
            handleError(response);
          }
        })
        .catch((error: any) => {
          console.error(error);
          handleError(error);
        });
    }
  };

  const handleSuccess = (successData: any) => {
    switch (successData.status) {
      case 'SUCCESS':
        setMessage(successData.message);
        break;
      case 'SUCCESS_WITH_MAPPING_ERRORS':
        //Set success message with status message and show go to mappings button
        setMessage(successData.message);
        setHasMappingIssues(true);
        break;

      default:
        //Set generic success message
        setMessage(successData.message);
        break;
    }

    setHasError(false);
    setIsLoading(false);
    setFile(null);
  };
  const handleError = (error: any) => {
    if (error.status) {
      switch (error.status) {
        case 'FAILURE_WITH_VALIDATION_ERRORS':
          downloadErrorsFile(error.message);
          setMessage(
            'We dectected some errors on your upload that have been automatically downloaded to your computer. Please correct these issues and try again.'
          );
          break;
        case 'FAILURE':
          //Set error message with status message
          setMessage(error.message);
          break;

        default:
          //Set generic something went wrong message
          setMessage('something went wrong');
          break;
      }
    } else {
      //Set generic something went wrong message
      setMessage('something went wrong');
    }

    setHasError(true);
    setIsLoading(false);
  };
  const downloadErrorsFile = async (fileName: string) => {
    const params = {
      fileName: fileName
    };
    try {
      const results = await fetchErrorsFile(params);
      const blob = new Blob([results]);
      saveAs(blob, fileName);
    } catch (error) {
      console.error(error);
      setMessage('something went wrong');
    }
  };

  const inputClasses =
    'block cursor-pointer first:mr-3 rounded font-nunitoBold text-sm leading-[21px] px-6 py-2 focus:outline-none bg-navy border-transparent text-white';
  const modalHeadlineStyles = 'font-bold text-[22px] inline-block mr-2 mb-8';

  return (
    <div className="block py-2">
      <div className="flex">
        {file ? (
          <div>
            <div className="inline-block mr-8">
              <p className="font-nunitoLight text-sm inline-block mr-2">{file.name}</p>
              <BaseButton variant="plain" onClick={() => setFile(null)}>
                <span>X</span>
              </BaseButton>
            </div>
            <div className="inline-block">
              <BaseButton variant="tertiary" onClick={doFileUpload}>
                <span>{uploadLabel}</span>
              </BaseButton>
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="file-upload" className={inputClasses}>
              {chooseLabel}
            </label>
            <input className="hidden" type="file" id="file-upload" ref={fileInputField} onChange={onFileChange}></input>
          </div>
        )}
      </div>
      {showModal && (
        <MessageModal isLoading={isLoading} closeModal={() => setShowModal(false)}>
          <div>
            {hasError ? (
              <div>
                <p className={modalHeadlineStyles}>File Upload Unsuccessful</p>
                <WarningIcon className={'inline-block fill-[#ED2323] mb-[5px]'} />
              </div>
            ) : (
              <div>
                <p className={modalHeadlineStyles}>File Uploaded Successfully</p>
                <CheckIcon className={'inline-block fill-[#009965] w-[20px] h-[20px] mb-[4px]'} />
              </div>
            )}

            <p>{message}</p>

            {hasMappingIssues && (
              <div className="mt-8 pt-4 border-t border-borderGray">
                <BaseButton variant="tertiary" onClick={() => history.push('/account/data-management/mapping')}>
                  Update Mapping
                </BaseButton>
              </div>
            )}
          </div>
        </MessageModal>
      )}
    </div>
  );
};

export default FileUpload;
